import { Check } from 'lucide-react';
import { ButtonHTMLAttributes, FC, ReactNode } from 'react';

import { PunchupButton, PunchupButtonProps } from 'features/common/components/Punchup/primitives/buttons/PunchupButton';
import { PunchupSpinner } from 'features/common/components/Punchup/primitives/feedback/PunchupSpinner';
import { PunchupDetailText } from 'features/common/components/Punchup/composites/content/PunchupDetailText';
import {
	PunchupDialogue,
	DialogIcon,
	DialogTitle,
	ButtonGroup,
	DIALOG_SPINNER_SIZE,
} from 'features/common/components/Punchup/composites/dialogs/PunchupDialogue';
import styles from './PunchupConfirmDialog.module.css';

export interface ConfirmDialogButton {
	label: string;
	onClick: () => void;
	variant?: PunchupButtonProps['variant'];
	type?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
	disabled?: boolean;
	/**
	 * Optional leading icon override forwarded to `PunchupButton`. Pass to replace the
	 * `destructive` variant's default `Trash2` on confirm buttons that are not deletions
	 * (e.g. seats manual-mode activation). Omit to keep the variant's conventional icon.
	 */
	icon?: ReactNode;
}

export interface PunchupConfirmDialogProps {
	title: string;
	/** Primary body text — black. Displayed as the main prompt. */
	text: string;
	buttons: ConfirmDialogButton[];
	/** Icon rendered inside the circle. Defaults to a checkmark. Pass a Lucide icon component. */
	icon?: ReactNode;
	/** Controls the icon circle colour. Default 'primary' (orange). Use 'destructive' (red) for cancel/delete actions. */
	iconVariant?: 'primary' | 'destructive';
	/** Passed through to PunchupDialogue. Default 400. */
	maxWidth?: number;
	/** Optional supplementary explanation shown below the main text in muted/smaller style. */
	secondaryText?: string;
	/** When true the secondaryText is hidden behind a collapsible "More info" toggle. Default false (always visible). */
	collapsibleSecondary?: boolean;
	/** When true, covers the dialog content with a semi-transparent overlay and spinner, and disables action buttons. Set once the API call is dispatched; clear on success or error. */
	isLoading?: boolean;
	/** When true (with isLoading), disables ALL buttons including the secondary cancel button. Use for financial mutations (Cancel Ticket, Refund) where closing mid-flight is ambiguous. Non-destructive operations leave the cancel path open. */
	isDestructive?: boolean;
}

const resolveButtonDisabled = (btn: ConfirmDialogButton, isLoading: boolean, isDestructive: boolean): boolean => {
	if (btn.disabled) return true;
	if (!isLoading) return false;
	if (isDestructive) return true;
	/* secondary = the cancel/abort path — stays clickable during non-destructive loading */
	const isAbortVariant = (btn.variant ?? 'secondary') === 'secondary';
	return !isAbortVariant;
};

export const PunchupConfirmDialog: FC<PunchupConfirmDialogProps> = ({
	icon,
	iconVariant = 'primary',
	title,
	text,
	secondaryText,
	collapsibleSecondary = false,
	buttons,
	maxWidth,
	isLoading = false,
	isDestructive = false,
}) => (
	<PunchupDialogue maxWidth={maxWidth}>
		<div className={styles.contentWrapper} aria-busy={isLoading || undefined}>
			{isLoading && (
				<div className={styles.loadingOverlay}>
					<PunchupSpinner size={DIALOG_SPINNER_SIZE} />
				</div>
			)}
			<DialogIcon variant={iconVariant}>{icon ?? <Check />}</DialogIcon>
			<DialogTitle>{title}</DialogTitle>
			{secondaryText ? (
				<PunchupDetailText main={text} secondary={secondaryText} collapsible={collapsibleSecondary} />
			) : (
				<p className={styles.text}>{text}</p>
			)}
		</div>
		<ButtonGroup>
			{buttons.map((btn) => (
				<PunchupButton
					key={btn.label}
					type={btn.type ?? 'button'}
					variant={btn.variant ?? 'secondary'}
					onClick={btn.onClick}
					icon={btn.icon}
					disabled={resolveButtonDisabled(btn, isLoading, isDestructive)}
				>
					{btn.label}
				</PunchupButton>
			))}
		</ButtonGroup>
	</PunchupDialogue>
);
